import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import type { SoftphoneRingStrategyModel } from '../../constants/softphone-ring-strategies';
import { BaseEntity } from './base.entity';
import { CallEntity } from './call.entity';
import { UserEntity } from './user.entity';

/**
 * Per-rep ring attempt during a multi-rep ring distribution. One row
 * per (call, rep, attempt) — a single call may produce many rows when
 * a round-robin or longest-idle strategy walks down the list looking
 * for an answer, or when a simultaneous ring fires N parallel
 * participants.create calls.
 *
 * The orchestrator (`SoftphoneRingOrchestrationService`) writes these
 * rows to drive two things:
 *   1. **Auto-advance state** — "which reps have we already tried for
 *      this call?" The next-attempt selector excludes them at the SQL
 *      layer rather than re-reading + re-filtering JS-side.
 *   2. **Audit / reporting** — "for which rings did rep X get tried
 *      first?" "What's our average ring-attempts-to-answer per group?"
 *      The orchestrator captures the strategy on each attempt so a
 *      report can break down by strategy without joining back to the
 *      destination config (which may have changed since the call).
 *
 * `targetCallSid` is set when the attempt fired via the REST
 * `participants.create` API (transfers) — the participant-join /
 * participant-leave handlers match against it to record the
 * attempt's outcome. For inbound TwiML attempts there is no
 * participant.create call (Twilio dials the rep directly via
 * `<Client>`); the action-callback handler on the `<Dial>` matches
 * by (callSid, attemptedAt) instead, so `targetCallSid` stays null.
 *
 * `outcome` is null while the attempt is in flight — flips to one of
 * the terminal values when the attempt resolves.
 */
@Index('softphone_call_ring_attempt_call_idx', ['fkCallId'])
@Index('softphone_call_ring_attempt_target_user_idx', ['targetUserId'])
@Index('softphone_call_ring_attempt_target_call_sid_idx', ['targetCallSid'])
@Entity('softphone_call_ring_attempt')
export class SoftphoneCallRingAttemptEntity extends BaseEntity {
	@Column('int', { name: 'fk_call__id', unsigned: true })
	public fkCallId!: number;

	/**
	 * Sequence number within this call, starting at 1. Simultaneous-ring
	 * attempts that fan out across N reps share an `attemptNumber`
	 * (they're one logical "round" with N parallel rings); round-robin /
	 * longest-idle attempts increment per ring (each is its own round).
	 */
	@Column('int', { name: 'attemptNumber', unsigned: true })
	public attemptNumber!: number;

	@Column('int', { name: 'targetUserId', unsigned: true })
	public targetUserId!: number;

	@Column('varchar', { name: 'targetCallSid', nullable: true, length: 64 })
	public targetCallSid!: string | null;

	/**
	 * Strategy in effect when this attempt fired. Denormalised so a
	 * later admin change to the destination config doesn't rewrite
	 * history.
	 */
	@Column('varchar', { name: 'strategy', length: 32 })
	public strategy!: SoftphoneRingStrategyModel;

	@Column('datetime', { name: 'attemptedAt' })
	public attemptedAt!: Date;

	@Column('datetime', { name: 'endedAt', nullable: true })
	public endedAt!: Date | null;

	/**
	 * `answered`  — rep accepted the call (terminal success).
	 * `no-answer` — rep didn't pick up before the strategy timeout.
	 * `canceled`  — orchestrator yanked this attempt (e.g. another
	 *               rep in a simultaneous ring won, so we cancel
	 *               the losers).
	 * `failed`    — Twilio reported failed/busy/etc on the dial.
	 * `null`      — attempt is still in flight.
	 */
	@Column('varchar', { name: 'outcome', nullable: true, length: 16 })
	public outcome!: 'answered' | 'no-answer' | 'canceled' | 'failed' | null;

	@ManyToOne(() => CallEntity, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: 'fk_call__id', referencedColumnName: 'id' }])
	public fkCall!: CallEntity;

	@ManyToOne(() => UserEntity, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: 'targetUserId', referencedColumnName: 'id' }])
	public targetUser!: UserEntity;
}
